import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { WBMFormatMoney } from "wbm-format-money";
import NavigationTokens from "../../constants/navigationTokens";
import { CheckOutResponseType } from "../../enums/CheckOutResponseType";
import { IOrderAwaitingApproval } from "../../interfaces/IOrderAwaitingApproval";
import DateService from "../../utilities/dateService";
import OrderApprovalService from "../../utilities/orderApprovalService";
import Icon from "../icon/icon";
import NotificationIcon from "./notificationIcon";
import "./notifications.scss";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ModalPopup } from "wbm-generic-components";
import { IOrderApprovalResponse } from "../../interfaces/IOrderApprovalResponse";
import { IApprovalError } from "../../interfaces/IApprovalError";
import { ICouponErrorInternal } from "../../interfaces/ICouponErrorInternal";
import { ICouponError } from "../../interfaces/ICouponError";
import { CouponValidation } from "../../enums/CouponValidation";

export interface IOrderApprovalProps {
  expandedOrderApproval?: string;
  approval: IOrderAwaitingApproval;
  expand: (guid: string) => void;
  isCookied: boolean;
  navTokens: Record<string, string>;
  preventCheckoutMessage: string;
  loginHref: string;
}

function OrderApproval(props: IOrderApprovalProps) {
  const isOpen =
    props.expandedOrderApproval ===
    props.approval.awaitingOrderIdentifier.approvalGuid;

  const [isApproved, setIsApproved] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [approverErrorMessage, setApproverErrorMessage] = useState("");
  const [approverErrorMessageCoupons, setApproverErrorMessageCoupons] =
    useState<ICouponErrorInternal[]>([]);
  const [animateAway, setAnimateAway] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [hideMenu, setHideMenu] = useState(false);

  const setApprovalError = useCallback(
    (approvalError: IOrderApprovalResponse) => {
      setApproverErrorMessageCoupons([]);
      let errorMessage = "";

      switch (approvalError.checkOutResponseType) {
        case CheckOutResponseType.CCAuthFailed:
          errorMessage =
            "The credit card provided was not approved by the card issuer. Please modify order and resubmit.";
          break;
        case CheckOutResponseType.FailedToCreateSO:
          errorMessage =
            "An error occurred while processing your order. Please try again. If the problem persists, call 1-888-WB-MASON for further assistance.";
          break;
        case CheckOutResponseType.PreventCheckout:
          errorMessage =
            "This order could not be approved due to your organization's purchasing rules.";
          break;
        case CheckOutResponseType.AlreadyReviewed:
          errorMessage = "This order has already been reviewed.";

          setHideMenu(true);
          setIsCompleted(true);
          setTimeout(() => {
            setAnimateAway(true);

            OrderApprovalService.completeOrder(
              props.approval.awaitingOrderIdentifier.approvalGuid,
              {
                approvalGuid:
                  props.approval.awaitingOrderIdentifier.approvalGuid,
                checkOutRequestType: approvalError.checkOutResponseType,
                cartId: props.approval.awaitingOrderIdentifier.cartId,
              }
            );

            OrderApprovalService.orderApproveReject(
              props.approval.awaitingOrderIdentifier.approvalGuid
            );

            OrderApprovalService.emitGetMoreOrders();
          }, 2000);
          break;
        case CheckOutResponseType.CurrentlyBeingReviewed:
          errorMessage = "This order is currently being reviewed.";
          break;
        case CheckOutResponseType.EmptyCart:
          errorMessage =
            "This order has no items. Please add items to the order before approving.";
          break;
        case CheckOutResponseType.InvalidCoupon:
          const approvalCouponError = approvalError.couponErrors;
          const couponErrors: ICouponErrorInternal[] = [];

          approvalCouponError.forEach((coupon) => {
            const couponError = couponErrors.filter((a) => {
              return a.couponId === coupon.couponId;
            })[0];

            if (!couponError) {
              const inValidCouponMessage = getInvalidCouponMessage(coupon);

              couponErrors.push({
                couponId: coupon.couponId,
                couponValidation: coupon.couponValidation,
                inValidCouponMessage: inValidCouponMessage,
              });
            } else {
              const index = couponErrors.findIndex(
                (a: ICouponErrorInternal) => a.couponId === coupon.couponId
              );

              if (coupon.couponValidation < couponError.couponValidation) {
                const inValidCouponMessage = getInvalidCouponMessage(coupon);

                couponErrors[index] = {
                  couponId: coupon.couponId,
                  couponValidation: coupon.couponValidation,
                  inValidCouponMessage: inValidCouponMessage,
                };
              }
            }

            setApproverErrorMessageCoupons([...couponErrors]);
          });
          break;
        case CheckOutResponseType.DeliveryChargeChanged:
          if (!props.approval.lockedByName) {
            errorMessage =
              "Your approval could not be processed due to a change in the delivery charge. The order has now been updated to reflect this. Clicking 'Approve' again will submit the order with the latest charge.";
          }
          break;
        default:
          errorMessage = props.preventCheckoutMessage;
          break;
      }

      if (!errorMessage && props.approval.lockedByName) {
        errorMessage = `Items being edited by ${props.approval.lockedByName}.`;
      }

      setApproverErrorMessage(errorMessage);
    },
    [props.preventCheckoutMessage, props.approval]
  );

  const getInvalidCouponMessage = (coupon: ICouponError) => {
    var inValidCouponMessage =
      "The following coupon has been removed from your order: " +
      coupon.couponId;

    switch (coupon.couponValidation) {
      case CouponValidation.InvalidAll:
        inValidCouponMessage += " - Invalid Coupon.";
        break;
      case CouponValidation.InvalidConfig:
        inValidCouponMessage +=
          " - This coupon is not available for your account.";
        break;
      case CouponValidation.InvalidDate:
        inValidCouponMessage += " - This coupon has expired.";
        break;
      case CouponValidation.InvalidOncePer:
        inValidCouponMessage +=
          " - This coupon has already been used on another order.";
        break;
      case CouponValidation.InvalidMultiple:
        inValidCouponMessage +=
          " - This coupon cannot be used in conjuction with the one already applied to the order.";
        break;
      case CouponValidation.InvalidThreshold:
        inValidCouponMessage +=
          " - The coupon order criteria has not been met.";
        break;
    }

    inValidCouponMessage +=
      " Clicking Approve again will submit this order without the coupon.";

    return inValidCouponMessage;
  };

  const handleErrors = useCallback(() => {
    const approvalError = OrderApprovalService.getApprovalError(
      props.approval.awaitingOrderIdentifier.approvalGuid
    );

    if (approvalError) {
      setApprovalError(approvalError);
    } else {
      if (props.approval.lockedByName) {
        setApproverErrorMessage(
          `Items being edited by ${props.approval.lockedByName}.`
        );
      } else {
        setApproverErrorMessage("");
      }
    }
  }, [
    props.approval.awaitingOrderIdentifier.approvalGuid,
    props.approval.lockedByName,
    setApproverErrorMessage,
    setApprovalError,
  ]);

  useEffect(() => {
    handleErrors();
  }, [handleErrors]);

  useEffect(() => {
    if (props.approval.retrievalTime) {
      handleErrors();
    }
  }, [props.approval.retrievalTime, handleErrors]);

  useEffect(() => {
    const approvalErrorsSub = OrderApprovalService.getApprovalErrorsObservable(
      props.approval.awaitingOrderIdentifier.approvalGuid
    ).subscribe((response: IApprovalError) => {
      if (response) {
        setIsApproved(false);
        setIsRejected(false);
        setIsCompleted(false);

        setApprovalError(response.response);
      }
    });

    return () => {
      approvalErrorsSub.unsubscribe();
    };
  }, [
    setIsApproved,
    setIsRejected,
    setIsCompleted,
    setApprovalError,
    props.approval.awaitingOrderIdentifier,
  ]);

  useEffect(() => {
    const completeApprovalSub = OrderApprovalService.getCompleteOrderObservable(
      props.approval.awaitingOrderIdentifier.approvalGuid
    ).subscribe(() => {
      setTimeout(() => {
        setAnimateAway(true);
      }, 2000);

      setIsCompleted(true);
    });

    return () => {
      completeApprovalSub.unsubscribe();
    };
  }, [
    setIsCompleted,
    setAnimateAway,
    props.approval.awaitingOrderIdentifier.approvalGuid,
  ]);

  const getTotal = (value: string) => {
    if (!value) {
      return 0.0;
    }
    return parseFloat(value.replace(/,/g, "").replace("$", ""));
  };

  const formatDate = (value: string) => {
    if (!value) {
      return "";
    }

    const tokens = value.split(" - ");

    if (tokens.length === 0) {
      return "";
    }

    const date = tokens[0].split("/");
    const time = tokens[1];

    if (!date || date.length < 3 || !time || time.indexOf(":") === -1) {
      return "";
    }

    const hours = parseInt(time.split(":")[0]);
    const minutes = parseInt(time.split(":")[1].split(" ")[0]);
    let hourOffset = 0;

    if (time.toLowerCase().indexOf("pm") !== -1 && hours !== 12) {
      hourOffset += 12;
    }

    const d = new Date(
      parseInt(date[2]),
      parseInt(date[0]) - 1,
      parseInt(date[1]),
      hours + hourOffset,
      minutes
    );

    return DateService.toMonthDateYearTimeEstString(d);
  };

  const approveReject = (isApproval: boolean) => {
    if (isApproval) {
      setIsApproved(true);
      emitOrder(isApproval);
    } else {
      setShowRejectModal(true);
    }
  };

  const emitOrder = (isApproval: boolean) => {
    OrderApprovalService.emit({
      approvalGuid: props.approval.awaitingOrderIdentifier.approvalGuid,
      checkOutRequestType: isApproval ? 0 : 1,
      cartId: props.approval.awaitingOrderIdentifier.cartId,
    });
  };

  const reject = () => {
    approveReject(false);
  };

  const approve = () => {
    approveReject(true);
  };

  return (
    <>
      {showRejectModal && (
        <ModalPopup
          actionItem={() => {
            setShowRejectModal(false);
            setIsRejected(true);
            emitOrder(false);
          }}
          actionItemText="Yes"
          boxShadow
          btnClass="btn-primary"
          closeIcon
          header="Reject Order"
          setShow
          className="header-app confirm-popup"
          onClose={() => {
            setShowRejectModal(false);
          }}
          showCancelButton
        >
          Are you sure you want to reject the selected order?
        </ModalPopup>
      )}
      <AnimateHeight
        className="order-approval"
        duration={300}
        height={animateAway ? 0 : "auto"}
        style={isOpen ? { background: "#047BC10D" } : {}}
      >
        <motion.div
          className={"container pt-2"}
          animate={
            animateAway
              ? {
                  opacity: 0,
                  transition: {
                    duration: 0.3,
                  },
                }
              : {}
          }
          key={props.approval.awaitingOrderIdentifier.awaitingOrderIdentifier}
        >
          <div className={"order-approval-container"}>
            {props.approval.viewed === false && (
              <NotificationIcon></NotificationIcon>
            )}
            <div
              onClick={() => {
                props.expand(
                  props.approval.awaitingOrderIdentifier.approvalGuid
                );
              }}
            >
              <div className="row">
                <div className="col-8">
                  <b>Order Number</b>{" "}
                  <span>
                    {props.approval.awaitingOrderIdentifier.orderNumber}
                  </span>
                </div>
                <div className="col-4 text-right">
                  <WBMFormatMoney
                    isFancy
                    amount={getTotal(props.approval.total)}
                    className="font-weight-bold"
                  />
                </div>
              </div>
              <div className={"row " + (props.isCookied ? "mb-10" : "mb-20")}>
                <div className="col-9 notifications-order-date">
                  {formatDate(props.approval.date)}
                </div>
                <div className="col-3 text-right">
                  <Icon
                    className={
                      "animate-arrow rotate-chevron" +
                      (isOpen ? " selected" : "")
                    }
                  >
                    keyboard_arrow_down
                  </Icon>
                </div>
              </div>
            </div>
            {approverErrorMessage && (
              <div className="text-danger font-weight-bold">
                {approverErrorMessage}
              </div>
            )}
            {approverErrorMessageCoupons.map((coupon, index) => {
              return (
                <div className="text-danger font-weight-bold" key={index}>
                  {coupon.inValidCouponMessage}
                </div>
              );
            })}
            <AnimatePresence>
              {!isApproved && !isRejected && !hideMenu && (
                <motion.div
                  className="row order-approval-actions-bar position-absolute"
                  key="controls"
                  exit="fade"
                  animate="appear"
                  initial="appear"
                  variants={{
                    fade: {
                      opacity: 0,
                      transition: {
                        duration: 0.2,
                      },
                    },
                    appear: {
                      opacity: 1,
                      transition: {
                        duration: 0.2,
                      },
                    },
                  }}
                >
                  {props.isCookied ? (
                    <>
                      {props.loginHref && (
                        <a
                          className="btn btn-block btn-primary d-block"
                          href={props.loginHref}
                        >
                          SIGN IN TO REVIEW
                        </a>
                      )}
                    </>
                  ) : (
                    <>
                      {!props.approval.lockedByName && (
                        <div
                          className={
                            props.approval.requireCheckout
                              ? "col-2 pl-0 pr-0"
                              : "col-4"
                          }
                        >
                          <a
                            className={`notifications-link-button ${isOpen ? " open": "" }`}
                            href={
                              props.navTokens[
                                NavigationTokens.OrderAwaitingApprovalDetails
                              ] +
                              `?SID=${props.approval.awaitingOrderIdentifier.orderNumber}&CartID=${props.approval.awaitingOrderIdentifier.cartId}`
                            }
                          >
                            <Icon className="mr-1">edit</Icon>
                            <span className="action-label">Edit</span>
                          </a>
                        </div>
                      )}
                      {props.approval.requireCheckout &&
                        !props.approval.lockedByName && (
                          <div className="col-10 pr-0">
                            <span className="text-danger font-weight-bold">
                              You must finish editing Shipping/Billing.
                            </span>
                          </div>
                        )}
                      {!props.approval.requireCheckout &&
                        !props.approval.lockedByName && (
                          <>
                            <div className="col-4">
                              <button
                                className={`notifications-link-button pl-0 pr-0 ${isOpen ? " open": "" }`}
                                onClick={() => {
                                  reject();
                                }}
                              >
                                <Icon className="mr-1">highlight_off</Icon>
                                <span className="action-label">Reject</span>
                              </button>
                            </div>
                            <div className="col-4 approve-button text-right">
                              <button
                                className="notifications-approve-button"
                                onClick={() => {
                                  approve();
                                }}
                              >
                                <b>APPROVE</b> <Icon>done</Icon>
                              </button>
                            </div>
                          </>
                        )}
                    </>
                  )}
                </motion.div>
              )}
              {isApproved && isCompleted && (
                <motion.div
                  key="approved"
                  exit="fade"
                  animate="appear"
                  initial="fade"
                  className="order-approval-actions-bar is-approved position-absolute"
                  variants={{
                    fade: {
                      opacity: 0,
                      y: -10,
                    },
                    appear: {
                      opacity: 1,
                      y: 0,
                    },
                  }}
                >
                  <b>Approved</b> <Icon>check</Icon>
                </motion.div>
              )}
              {isRejected && isCompleted && (
                <motion.div
                  key="rejected"
                  exit="fade"
                  animate="appear"
                  initial="fade"
                  className="order-approval-actions-bar is-rejected position-absolute"
                  variants={{
                    fade: {
                      opacity: 0,
                      y: -10,
                    },
                    appear: {
                      opacity: 1,
                      y: 0,
                    },
                  }}
                >
                  <Icon>close</Icon> <b>Rejected</b>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="order-approval-actions-bar mb-2"></div>
            <div
              className={
                "notification-divider mb-2 " + (props.isCookied ? "mt-20" : "")
              }
            ></div>
            <AnimateHeight duration={200} height={isOpen ? "auto" : 0}>
              <div className="order-approval-extras">
                <div>
                  <b>Ordered By:</b> <span>{props.approval.orderedBy}</span>
                </div>
                <div>
                  <b>Account/Group:</b>{" "}
                  <span>{props.approval.accountAndGroup}</span>
                </div>
                <div>
                  <b>Address: </b> <span>{props.approval.deliveryName}</span>{" "}
                  <span>
                    {props.approval.deliveryStreet},{" "}
                    {props.approval.deliveryCity},{" "}
                    {props.approval.deliveryState} {props.approval.deliveryZip}
                  </span>
                </div>
              </div>
            </AnimateHeight>
          </div>
        </motion.div>
      </AnimateHeight>
    </>
  );
}

export default OrderApproval;
